import { useCallback, useEffect, useState, type RefObject } from 'react'

/**
 * Hook to detect horizontal scroll state of a container.
 * Uses scroll events and ResizeObserver for accurate detection.
 */
export function useHorizontalScroll(ref: RefObject<HTMLElement | null>): {
  canScrollLeft: boolean
  canScrollRight: boolean
} {
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const check = useCallback(() => {
    const el = ref.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [ref])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    check()

    el.addEventListener('scroll', check, { passive: true })

    const ro = new ResizeObserver(check)
    ro.observe(el)
    // Also observe children so we detect when content width changes
    for (const child of Array.from(el.children)) {
      ro.observe(child)
    }

    return () => {
      el.removeEventListener('scroll', check)
      ro.disconnect()
    }
  }, [ref, check])

  return { canScrollLeft, canScrollRight }
}
